import { Maybe, RestOptions } from '@csp/csp-common-model';
import { Page } from '@csp/dmdp-api-common-dto';
import {
  ArchiveFileBundleInV1,
  ArchiveFileClassificationAggregationInV1,
  ArchiveFileClassificationAggregationV1,
  ArchiveFileQueryV1,
  ArchiveFilesV1,
} from '@csp/dmdp-api-file-storage-archiving-dto';
import type { AxiosResponse } from 'axios';
import { fileStorageUrl } from '../../dmdp-config/dmdpConfig';
import { BinaryFileData } from '../model/BinaryFileData';

const archivingUrl = (): string => `${fileStorageUrl()}/archiving/v1/archive-files`;
const archivingBundleUrl = (): string => `${fileStorageUrl()}/archiving/v1/archive-file-bundles`;

const queryArchiveFilesUrl = (): string => `${archivingUrl()}:query`;
const queryArchiveFiles = async (
  { signal, axios }: RestOptions,
  query: ArchiveFileQueryV1,
  page?: Page,
): Promise<ArchiveFilesV1> => {
  const { data } = await axios.post<ArchiveFilesV1>(queryArchiveFilesUrl(), query, {
    params: page,
    signal,
  });
  return data;
};

const getAggregatedClassificationsUrl = (): string => `${archivingUrl()}:aggregate-classification`;
const getAggregatedClassifications = async (
  { signal, axios }: RestOptions,
  params: ArchiveFileClassificationAggregationInV1,
): Promise<ArchiveFileClassificationAggregationV1[]> => {
  const { data } = await axios.post<ArchiveFileClassificationAggregationV1[]>(
    getAggregatedClassificationsUrl(),
    params,
    { signal },
  );
  return data;
};

const getBinaryFileDataUrl = (archiveFileId: string): string => `${archivingUrl()}/${archiveFileId}/file-data`;
const getBinaryFileData = async ({ signal, axios }: RestOptions, archiveFileId: string): Promise<BinaryFileData> => {
  const response = await axios.get(getBinaryFileDataUrl(archiveFileId), {
    signal,
    responseType: 'blob',
  });
  return {
    blob: response.data as Blob,
    contentType: (response.headers['content-type'] ?? 'text/plain') as string,
  };
};

const createSiteFilesBundleUrl = (): string => archivingBundleUrl();
const createSiteFilesBundle = async (
  { signal, axios }: RestOptions,
  orgId: ArchiveFileBundleInV1,
): Promise<Maybe<string>> => {
  const response = await axios.post(createSiteFilesBundleUrl(), orgId, {
    signal,
  });

  return response.headers['location'];
};

const downloadSiteFilesBundleUrl = (archiveBundleUrl: string): string => `${fileStorageUrl()}${archiveBundleUrl}`;
const downloadSiteFilesBundle = async (
  { signal, axios }: RestOptions,
  archiveBundleUrl: string,
): Promise<AxiosResponse<Blob>> =>
  axios.get(downloadSiteFilesBundleUrl(archiveBundleUrl), { signal, responseType: 'blob' });

export const ArchivingRestServiceV1 = {
  queryArchiveFiles,
  queryArchiveFilesUrl,
  getBinaryFileDataUrl,
  getBinaryFileData,
  createSiteFilesBundleUrl,
  createSiteFilesBundle,
  downloadSiteFilesBundleUrl,
  downloadSiteFilesBundle,
  getAggregatedClassificationsUrl,
  getAggregatedClassifications,
};
